import { Box, BottomSheetHeader, Button, Modal, Pressable } from "../../../../../../../mads";
import { Close, Upload } from "../../../../../../../mads/icons";
import { useI18n } from "../../../../../../../shared/I18n";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, Platform, StyleSheet } from "react-native";

// Matches the shared BottomSheet's offset
const OFFSCREEN_OFFSET = 1000;

const OPEN_DURATION_MS = 300;
const CLOSE_DURATION_MS = 200;

// Safety net for the iOS deferral below. Mirrors ghostwriter's ApplicationFilePickerField.
const DISMISS_FALLBACK_MS = 700;

export interface AddResumeBottomSheetProps {
  /**
   * Drives the underlying Modal. Keep this component mounted and toggle
   * `visible` instead of conditionally rendering it: iOS only delivers
   * `Modal.onDismiss` while the Modal stays mounted, and that callback is what
   * tells us the sheet's view controller is really gone.
   */
  visible: boolean;
  /** Called after the sheet closes, whether from a selection or a plain dismiss. */
  onClose: () => void;
  /** Called when the user selects "Build your resume" */
  onBuildPress: () => void;
  /** Called when the user selects "Upload your resume" */
  onUploadPress: () => void;
}

/**
 * Bottom sheet presenting the "Build your resume" / "Upload your resume" choices
 * for the mobile "Add your resume" CTA in GatedMatchOverview.
 *
 * Both choices present a native view controller (the document picker, the resume
 * builder), and iOS refuses to present one on top of a sheet that is still
 * presented — silently, so the picker simply never appears. So on iOS the chosen
 * action is stashed and run from `Modal.onDismiss`, which fires only once the
 * view controller is actually torn down. Android has no such constraint and
 * never fires `onDismiss`, so it acts immediately. Mirrors the same pattern in
 * PostApplyAd and ace's NbaOverviewScreen.
 *
 * The Modal uses `animationType="none"` so the sheet and its backdrop animate
 * independently — the sheet slides up via `translateY` while the dim only fades.
 * A single `animationType="slide"` would drag the dimming overlay up with the
 * sheet, which is the animation we want to avoid.
 */
export const AddResumeBottomSheet: React.FC<AddResumeBottomSheetProps> = ({
  visible,
  onClose,
  onBuildPress,
  onUploadPress,
}) => {
  const gt = useI18n();

  const translateY = useRef(new Animated.Value(OFFSCREEN_OFFSET)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);

  // Keeps the Modal mounted through the close animation. Mirrors `visible` but
  // trails it on close, so the slide-out can play and — crucially — the Modal is
  // still mounted when its `visible` flips false and iOS dismisses the VC.
  const [isModalVisible, setIsModalVisible] = useState(visible);
  const isModalVisibleRef = useRef(isModalVisible);
  isModalVisibleRef.current = isModalVisible;

  const pendingActionRef = useRef<(() => void) | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = () => {
    if (fallbackTimerRef.current !== null) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  };

  // Nulls the pending action before invoking it, so whichever of `onDismiss` or
  // the fallback timer arrives first wins and the other becomes a no-op.
  const runPendingAction = () => {
    clearFallbackTimer();
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  useEffect(() => {
    animationRef.current?.stop();

    if (visible) {
      // A reopen can race a previous dismissal whose `onDismiss` hasn't landed yet
      // (or whose fallback timer is still pending) — discard it so it can't fire a
      // stale action against this new presentation.
      clearFallbackTimer();
      pendingActionRef.current = null;

      setIsModalVisible(true);
      // Reset to the closed position so a reopen always slides in cleanly.
      translateY.setValue(OFFSCREEN_OFFSET);
      backdropOpacity.setValue(0);

      const easing = Platform.OS === "ios" ? Easing.out(Easing.cubic) : Easing.out(Easing.ease);
      const animation = Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration: OPEN_DURATION_MS,
          easing,
          useNativeDriver: true,
        }),
        Animated.timing(backdropOpacity, {
          toValue: 1,
          duration: OPEN_DURATION_MS,
          easing: Platform.OS === "ios" ? Easing.out(Easing.cubic) : Easing.ease,
          useNativeDriver: true,
        }),
      ]);

      animationRef.current = animation;
      animation.start(({ finished }) => {
        if (finished) {
          animationRef.current = null;
        }
      });
    } else if (isModalVisibleRef.current) {
      const animation = Animated.parallel([
        Animated.timing(translateY, {
          toValue: OFFSCREEN_OFFSET,
          duration: CLOSE_DURATION_MS,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(backdropOpacity, {
          toValue: 0,
          duration: CLOSE_DURATION_MS,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]);

      animationRef.current = animation;
      animation.start(({ finished }) => {
        if (!finished) return;
        animationRef.current = null;
        // Flipping the Modal's `visible` (without unmounting it) is what lets
        // iOS dismiss the view controller and report back via `onDismiss`.
        setIsModalVisible(false);

        if (pendingActionRef.current) {
          clearFallbackTimer();
          fallbackTimerRef.current = setTimeout(runPendingAction, DISMISS_FALLBACK_MS);
        }
      });
    }

    return () => {
      animationRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runPendingAction is stable in practice; re-running on identity changes would restart the animation
  }, [visible, backdropOpacity, translateY]);

  // Drop the fallback timer if we're torn down mid-dismissal. Deliberately does
  // not run the action: presenting a picker after this screen is gone is worse
  // than dropping it.
  useEffect(() => clearFallbackTimer, []);

  const selectOption = (action: () => void) => {
    if (Platform.OS === "ios") {
      pendingActionRef.current = action;
      onClose();
      return;
    }

    onClose();
    action();
  };

  const handleSelectUpload = () => selectOption(onUploadPress);

  const handleSelectBuild = () => selectOption(onBuildPress);

  const headerRight = (
    <Pressable
      onPress={onClose}
      accessibilityRole="button"
      accessibilityLabel={gt.gettext("Close")}
      hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
      testID="vj-gated-match-overview-sheet-close-button"
      sx={{ paddingHorizontal: spacing.s3 }}
    >
      <Close size="md" color={colors.foregroundStrong} />
    </Pressable>
  );

  return (
    <Modal
      visible={isModalVisible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={onClose}
      onDismiss={Platform.OS === "ios" ? runPendingAction : undefined}
      testID="vj-gated-match-overview-sheet-modal"
    >
      <Box sx={styles.overlay} accessibilityViewIsModal>
        <Pressable
          sx={styles.backdropPressable}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={gt.gettext("Close")}
          testID="vj-gated-match-overview-sheet-backdrop"
        >
          <Animated.View style={[styles.backdrop, { opacity: backdropOpacity }]} />
        </Pressable>

        <Animated.View
          style={[styles.sheet, { transform: [{ translateY }] }]}
          testID="vj-gated-match-overview-sheet"
        >
          <BottomSheetHeader
            title={gt.gettext("Add your resume")}
            headerRight={headerRight}
            showBorder={false}
            showGrabber={false}
          />
          <Box sx={styles.sheetOptions}>
            <Button
              onPress={handleSelectUpload}
              iconBefore={<Upload size="sm" color={colors.foregroundActionInverse} />}
            >
              {gt.gettext("Upload your resume")}
            </Button>
            <Button variant="secondary" onPress={handleSelectBuild}>
              {gt.gettext("Build your resume")}
            </Button>
          </Box>
        </Animated.View>
      </Box>
    </Modal>
  );
};

// Mirrors the shared BottomSheet's surface so this sheet stays visually
// identical to the one it replaced.
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  backdropPressable: {
    ...StyleSheet.absoluteFillObject,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    overflow: "hidden",
    maxHeight: "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  sheetOptions: {
    padding: spacing.s4,
    paddingBottom: spacing.s6,
    gap: spacing.s2,
  },
});
